import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { AuthContext } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import {
  getConversations,
  getConversation,
  sendMessage,
  markMessagesRead,
  getUser,
  checkFollowing,
  followUser,
  denyPendingConnection,
  getNetworkFollowers,
  getNetworkFollowing,
  uploadFile,
  getMediaUrl,
} from '../services/api';

function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(date, now)) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (sameDay(date, yesterday)) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}

const fetchConversations = async () => {
  const response = await getConversations();
  return response.data || [];
};

const getMessageIdentity = (message) =>
  [
    message.sender_id,
    message.receiver_id,
    message.content || '',
    message.media_url || '',
    message.media_type || '',
  ].join('|');

const CONVERSATION_REFRESH_INTERVAL = process.env.NODE_ENV === 'test' ? 0 : 30000;
const MESSAGE_REFRESH_INTERVAL = process.env.NODE_ENV === 'test' ? 0 : 15000;

function Messages() {
  const { userId } = useParams();
  const { user, fetchUnreadCount } = useContext(AuthContext);
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();

  const [newMessage, setNewMessage] = useState('');
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [chatMediaFile, setChatMediaFile] = useState(null);
  const [chatMediaPreview, setChatMediaPreview] = useState(null);
  const [chatMediaType, setChatMediaType] = useState(null);
  const [sending, setSending] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  const messagesEndRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const chatFileInputRef = useRef(null);

  const {
    data: conversations = [],
    error: conversationsError,
    mutate: refreshConversations,
  } = useSWR('messages/conversations', fetchConversations, {
    refreshInterval: CONVERSATION_REFRESH_INTERVAL,
  });

  const {
    data: loadedMessages = [],
    error: conversationError,
    mutate: refreshConversation,
  } = useSWR(
    userId ? ['messages/conversation', userId] : null,
    async ([, otherUserId]) => {
      const response = await getConversation(otherUserId);
      await markMessagesRead(otherUserId);
      fetchUnreadCount();
      return response.data || [];
    },
    { refreshInterval: MESSAGE_REFRESH_INTERVAL }
  );

  const messages = useMemo(() => {
    const loadedMessageIds = new Set(loadedMessages.map(getMessageIdentity));
    return [
      ...loadedMessages,
      ...optimisticMessages.filter((message) => !loadedMessageIds.has(getMessageIdentity(message))),
    ];
  }, [loadedMessages, optimisticMessages]);

  const selectedConversation = useMemo(() => {
    if (!userId) return null;
    return conversations.find((c) => c.user_id === parseInt(userId));
  }, [conversations, userId]);

  const { data: selectedUserData } = useSWR(
    userId && !selectedConversation ? ['messages/user', userId] : null,
    async ([, selectedUserId]) => {
      try {
        const res = await getUser(selectedUserId);
        const userData = res.data;
        return {
          user_id: parseInt(selectedUserId),
          full_name: userData.full_name,
          username: userData.username,
          profile_image: userData.profile_image,
          _isPlaceholder: true,
        };
      } catch {
        return { user_id: parseInt(selectedUserId), _isPlaceholder: true };
      }
    }
  );

  const selectedUser = userId
    ? selectedConversation ||
      selectedUserData || { user_id: parseInt(userId), _isPlaceholder: true }
    : null;

  const {
    data: connectionStatus,
    error: connectionStatusError,
    mutate: refreshConnectionStatus,
  } = useSWR(userId ? ['messages/connection-status', userId] : null, async ([, otherUserId]) => {
    const res = await checkFollowing(otherUserId);
    return res.data;
  });

  const conversationIdsKey = conversations
    .map((c) => c.user_id)
    .sort()
    .join(',');
  const {
    data: connections = [],
    error: connectionsError,
    isLoading: connectionsLoading,
  } = useSWR(showNewConvo ? ['messages/connections', conversationIdsKey] : null, async () => {
    const [followersRes, followingRes] = await Promise.all([
      getNetworkFollowers(),
      getNetworkFollowing(),
    ]);
    const followers = followersRes.data || [];
    const following = followingRes.data || [];
    const followerIds = new Set(followers.map((f) => f.id || f.user_id));
    const mutuals = following.filter((f) => followerIds.has(f.id || f.user_id));
    const existingIds = new Set(conversations.map((c) => c.user_id));
    return mutuals.filter((m) => !existingIds.has(m.id || m.user_id));
  });

  useEffect(() => {
    setOptimisticMessages([]);
  }, [userId]);

  useEffect(() => {
    if (!conversationsError) return;
    console.error('Error loading conversations:', conversationsError);
    showError('Failed to load conversations.');
  }, [conversationsError, showError]);

  useEffect(() => {
    if (!conversationError) return;
    console.error('Error loading conversation:', conversationError);
    showError('Failed to load messages.');
  }, [conversationError, showError]);

  useEffect(() => {
    if (connectionStatusError) {
      console.error('Error checking connection status:', connectionStatusError);
    }
  }, [connectionStatusError]);

  useEffect(() => {
    if (connectionsError) {
      console.error('Error loading connections:', connectionsError);
    }
  }, [connectionsError]);

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const handleNewConvo = () => {
    setShowNewConvo(true);
  };

  const handleStartConvo = (person) => {
    setShowNewConvo(false);
    navigate(`/messages/${person.id || person.user_id}`);
  };

  const handleSelectConversation = (conv) => {
    navigate(`/messages/${conv.user_id}`);
  };

  const handleConnect = async () => {
    try {
      await followUser(userId);
      const res = await checkFollowing(userId);
      refreshConnectionStatus(res.data, { revalidate: false });
      showSuccess(`You're now connected with ${selectedUser?.full_name || 'this user'}`);
      refreshConversations();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to connect');
    }
  };

  const handleDenyRequest = async () => {
    try {
      await denyPendingConnection(userId);
      refreshConnectionStatus(
        { isFollowing: false, isFollowedBy: false, isMutual: false },
        { revalidate: false }
      );
      showSuccess('Request dismissed');
      refreshConversations();
      navigate('/messages');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to dismiss request');
    }
  };

  const handleChatFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setChatMediaFile(file);
    setChatMediaType(file.type.startsWith('video') ? 'video' : 'image');
    const reader = new FileReader();
    reader.onload = (ev) => setChatMediaPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearChatMedia = () => {
    setChatMediaFile(null);
    setChatMediaPreview(null);
    setChatMediaType(null);
    if (chatFileInputRef.current) chatFileInputRef.current.value = '';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !chatMediaFile) || !userId) return;
    setSending(true);

    try {
      let mediaUrl = null;
      let mediaType = null;
      if (chatMediaFile) {
        const uploadRes = await uploadFile(chatMediaFile);
        mediaUrl = uploadRes.data?.url;
        mediaType = chatMediaType;
      }

      const messageData = {
        receiver_id: userId,
        content: newMessage || '',
        media_url: mediaUrl,
        media_type: mediaType,
      };

      await sendMessage(messageData);

      const newMsg = {
        id: `temp-${Date.now()}`,
        sender_id: user.id,
        receiver_id: parseInt(userId),
        content: newMessage || '',
        media_url: mediaUrl,
        media_type: mediaType,
        created_at: new Date().toISOString(),
      };

      setOptimisticMessages((prev) => [...prev, newMsg]);
      setNewMessage('');
      clearChatMedia();
      refreshConversation();
      refreshConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message. Please try again.');
    }
    setSending(false);
  };

  // Build display list: real conversations + placeholder for new conversation
  const displayConversations = (() => {
    if (!selectedUser?._isPlaceholder) return conversations;
    const alreadyInList = conversations.some((c) => c.user_id === selectedUser.user_id);
    if (alreadyInList) return conversations;
    return [{ ...selectedUser, last_message: null, unread_count: 0 }, ...conversations];
  })();

  return (
    <div className="messages-container">
      <div className="conversations-list">
        <div className="messages-header">
          <h2>Messages</h2>
          <button
            className="new-convo-btn"
            onClick={handleNewConvo}
            title="New conversation"
            aria-label="New conversation"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>

        {showNewConvo && (
          <div className="new-convo-panel">
            <div className="new-convo-panel-header">
              <span>Start a conversation</span>
              <button
                className="new-convo-close"
                onClick={() => setShowNewConvo(false)}
                aria-label="Close new conversation panel"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            {connectionsLoading ? (
              <p className="new-convo-loading">Loading connections...</p>
            ) : connections.length === 0 ? (
              <p className="new-convo-empty">No new connections to message</p>
            ) : (
              connections.map((person) => (
                <button
                  type="button"
                  key={person.id || person.user_id}
                  className="new-convo-person"
                  onClick={() => handleStartConvo(person)}
                >
                  <div className="conversation-avatar">
                    {(person.full_name || person.username)?.charAt(0).toUpperCase()}
                  </div>
                  <div className="new-convo-person-info">
                    <strong>{person.full_name || person.username}</strong>
                    <span>@{person.username}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {displayConversations.length === 0 && !showNewConvo ? (
          <div className="no-conversations">
            <p>No conversations yet</p>
            <button className="start-convo-btn" onClick={handleNewConvo}>
              Start a Conversation
            </button>
          </div>
        ) : (
          displayConversations.map((conv) => (
            <div
              key={conv.user_id}
              className={`conversation-item ${userId === String(conv.user_id) ? 'active' : ''}`}
              onClick={() => handleSelectConversation(conv)}
              role="button"
              tabIndex={0}
              aria-label={`Open conversation with ${conv.full_name || conv.username}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectConversation(conv);
                }
              }}
            >
              <div className="conversation-avatar">
                {conv.profile_image ? (
                  <img
                    src={getMediaUrl(conv.profile_image)}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%',
                    }}
                  />
                ) : (
                  (conv.full_name || conv.username)?.charAt(0).toUpperCase()
                )}
              </div>
              <div className="conversation-info">
                <div className="conversation-info-header">
                  <strong>{conv.full_name || conv.username}</strong>
                  {conv.last_message_time && (
                    <span className="conversation-time">{formatTime(conv.last_message_time)}</span>
                  )}
                </div>
                <p>
                  {conv.last_message?.substring(0, 40)}
                  {conv.last_message?.length > 40 ? '...' : ''}
                </p>
              </div>
              {conv.unread_count > 0 && <span className="unread-badge">{conv.unread_count}</span>}
            </div>
          ))
        )}
      </div>

      <div className="chat-window">
        {!userId ? (
          <div className="chat-empty">
            <div className="chat-empty-icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <p className="chat-empty-title">Messages</p>
            <p>Connect with someone to start messaging</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-header-avatar">
                {selectedUser?.profile_image ? (
                  <img src={getMediaUrl(selectedUser.profile_image)} alt="" />
                ) : (
                  (selectedUser?.full_name || selectedUser?.username)?.charAt(0).toUpperCase()
                )}
              </div>
              <span>{selectedUser?.full_name || selectedUser?.username || 'Chat'}</span>
            </div>
            <div className="messages-list">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message ${msg.sender_id === user.id ? 'sent' : 'received'}`}
                >
                  {msg.media_url && (
                    <div className="message-media">
                      {msg.media_type === 'video' ? (
                        <video
                          src={getMediaUrl(msg.media_url)}
                          controls
                          className="message-media-video"
                        />
                      ) : (
                        <img
                          src={getMediaUrl(msg.media_url)}
                          alt="Message attachment"
                          className="message-media-image"
                        />
                      )}
                    </div>
                  )}
                  {msg.content && <div className="message-content">{msg.content}</div>}
                  <div className="message-time">{formatTime(msg.created_at)}</div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {connectionStatus?.isMutual ? (
              <div className="chat-input-area">
                {chatMediaPreview && (
                  <div className="chat-media-preview">
                    {chatMediaType === 'video' ? (
                      <video src={chatMediaPreview} className="chat-preview-thumb" />
                    ) : (
                      <img
                        src={chatMediaPreview}
                        alt="Selected attachment preview"
                        className="chat-preview-thumb"
                      />
                    )}
                    <button
                      className="chat-preview-remove"
                      onClick={clearChatMedia}
                      aria-label="Remove attached media"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                )}
                <form className="chat-input" onSubmit={handleSendMessage}>
                  <button
                    type="button"
                    className="chat-attach-btn"
                    onClick={() => chatFileInputRef.current?.click()}
                    title="Attach file"
                    aria-label="Attach file"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                  <input
                    ref={chatFileInputRef}
                    type="file"
                    accept="image/*,video/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleChatFileSelect}
                    aria-label="Message attachment"
                  />
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    aria-label="Type a message"
                  />
                  <button type="submit" disabled={sending}>
                    {sending ? '...' : 'Send'}
                  </button>
                </form>
              </div>
            ) : connectionStatus?.isFollowing && !connectionStatus?.isFollowedBy ? (
              <div className="chat-connection-notice">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>
                  {selectedUser?.full_name || 'This user'} has to accept your request before you can
                  continue.
                </span>
              </div>
            ) : connectionStatus?.isFollowedBy && !connectionStatus?.isFollowing ? (
              <div className="chat-connect-prompt">
                <span>
                  To reply, accept {selectedUser?.full_name || 'this user'}'s connection request.
                </span>
                <div className="chat-connect-actions">
                  <button className="chat-connect-btn" onClick={handleConnect}>
                    Accept
                  </button>
                  <button className="chat-deny-btn" onClick={handleDenyRequest}>
                    Dismiss
                  </button>
                </div>
              </div>
            ) : (
              <div className="chat-connection-notice">
                <span>
                  Connect with {selectedUser?.full_name || 'this user'} to start messaging
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Messages;
